import { useEffect, useState } from "react";
import api from "../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Sparkles, Loader2, Check, Crown, Zap, Rocket } from "lucide-react";
import { toast } from "sonner";

const icons = {
  standard: Zap,
  premium: Rocket,
  elite: Crown,
};

export default function BoostModal({ event, open, onOpenChange }) {
  const [packages, setPackages] = useState([]);
  const [selected, setSelected] = useState("premium");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/boost/packages").then((r) => setPackages(r.data));
    }
  }, [open]);

  const handleCheckout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/boost/checkout", {
        event_id: event.id,
        package_id: selected,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de paiement");
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-cream border-ink/15" data-testid="boost-modal">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="h-10 w-10 bg-accent text-white grid place-items-center">
              <Sparkles className="h-4 w-4" />
            </div>
            <div>
              <DialogTitle className="font-heading font-light text-2xl tracking-editorial text-ink">
                Booster <span className="font-display italic">«&nbsp;{event?.title}&nbsp;»</span>
              </DialogTitle>
              <DialogDescription className="text-xs mt-1">
                Badge or « À la une » + sticky en haut de la page région
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-ink/10 mt-4">
          {packages.map((pkg) => {
            const Icon = icons[pkg.id] || Zap;
            const isActive = selected === pkg.id;
            return (
              <button
                key={pkg.id}
                type="button"
                data-testid={`boost-pkg-${pkg.id}`}
                onClick={() => setSelected(pkg.id)}
                className={`relative p-6 text-left transition-all ${
                  isActive ? "bg-ink text-cream" : "bg-white text-ink hover:bg-cream-50"
                }`}
              >
                {isActive && (
                  <div className="absolute top-3 right-3 h-5 w-5 bg-accent text-white rounded-full grid place-items-center">
                    <Check className="h-3 w-3" />
                  </div>
                )}
                <Icon className={`h-5 w-5 mb-4 ${isActive ? "text-accent" : "text-ink/40"}`} />
                <p className={`text-label mb-2 ${isActive ? "text-cream/60" : "text-ink/40"}`}>{pkg.name}</p>
                <p className="font-heading font-light text-4xl tracking-editorial">
                  <span className="font-display italic">{pkg.price}</span>€
                </p>
                <p className={`text-xs mt-1 ${isActive ? "text-cream/60" : "text-ink/50"}`}>
                  {pkg.duration_days} jours
                </p>
                <p className={`text-xs mt-4 leading-relaxed ${isActive ? "text-cream/70" : "text-ink/60"}`}>
                  {pkg.description}
                </p>
              </button>
            );
          })}
        </div>

        <div className="bg-white border border-ink/10 p-5 text-xs text-ink/65 mt-1">
          <p className="text-label text-ink mb-2">Comment ça marche ?</p>
          <ul className="space-y-1.5 leading-relaxed">
            <li>· Votre événement remonte automatiquement en haut de la page de sa région</li>
            <li>· Un badge or « À la une » apparaît sur sa fiche partout sur la plateforme</li>
            <li>· Paiement unique, pas d'abonnement</li>
            <li>· Le boost démarre dès le paiement confirmé</li>
          </ul>
        </div>

        <Button
          data-testid="boost-confirm"
          onClick={handleCheckout}
          disabled={loading}
          className="w-full bg-ink hover:bg-ink/85 text-cream rounded-none py-6 h-auto"
          size="lg"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
            <>
              Booster pour {packages.find((p) => p.id === selected)?.price}€
              <Sparkles className="h-4 w-4 ml-2" />
            </>
          )}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
